import { Box, Pagination, Stack } from '@mui/material';
import { MprcContainer, MprcRequestErrorDialog } from 'components';
import { MprcFilterCount } from 'components/filter';
import { MprcRibbonListCardsExpandedContainer } from 'components/MprcRibbonList';
import { MprcRibbonListCardsGridContainer } from 'components/MprcRibbonList/MprcRibbonListCardContainers';
import { CardDtoPaginatedList, CarouselCardRenderMode, CoreFilterCommand } from 'generated/CoreApi_v1';
import TzUtils from 'lib/TzUtils';
import { isCanceledError, useCoreApi } from 'providers/CoreApiProvider';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router";
import { GeneralCardLoading } from 'views/general/Cards';
import { CardDtoToCardRibbon } from 'views/general/Cards/CardDtoToCardRibbon';
import { CatalogFilter } from 'views/lms/student/Catalog/CatalogFilter';
import { getClearFilter, ICatalogFilterOptions } from 'views/lms/student/Catalog/FilterCommon';

/////////////////////////////////////////////////////////////////////////////

export type LmsCatalogInitialFilter = {
    topic?: {
        topicId: string;
        name: string;
    };
};

export const LmsCatalog = () => {
    const api = useCoreApi();
    const location = useLocation();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<CardDtoPaginatedList | null>(null);
    const [viewError, setViewError] = useState<any>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [viewFilter, setViewFilter] = useState<ICatalogFilterOptions>(getClearFilter());
    const [reload, setReload] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | undefined>(undefined);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const resetFilters = () => {
        setViewFilter(getClearFilter());
    };

    ////////////////////////////////////////////////////////////////////////////

    const filtered = viewFilter.searchText.length > 0 || viewFilter.advFilters.length > 0;

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////
    useEffect(() => {
        const { topic } = (location.state ?? {}) as LmsCatalogInitialFilter;
        if (topic) {
            setViewFilter({ ...viewFilter, advFilters: [{ command: CoreFilterCommand.TrainingTopic, value: topic.topicId, text: topic.name, quickFilter: null }] });
        }
    }, []);

    useEffect(() => {
        async function getData() {
            setLoading(true);

            try {
                abortControllerRef.current?.abort();

                abortControllerRef.current = new AbortController();

                const result = await api.v1.lmsStudent.getCatalog({
                    browserTimeZoneId: TzUtils.browserTimezone(),
                    filters: viewFilter.advFilters,
                    order: viewFilter.order,
                    searchText: viewFilter.searchText ?? null,
                    pageNumber: pageNumber,
                    pageSize: 18
                }, { signal: abortControllerRef.current.signal });

                setViewData(result.data);
                setLoading(false);
                setReload(false);
            } catch (e) {
                if (!isCanceledError(e)) {
                    setViewData(null);
                    setViewError(e);
                    setLoading(false);
                    setReload(false);
                }
            }
            finally {
                abortControllerRef.current = undefined;
            }
        }

        getData();

    }, [pageNumber, viewFilter, reload]);

    ///
    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <MprcRibbonListCardsExpandedContainer>

                <Box sx={{ mb: 2 }}>
                    <MprcContainer>
                        <CatalogFilter
                            viewFilter={viewFilter}
                            setViewFilter={(filter) => {
                                setViewFilter(filter);
                                setPageNumber(1);
                            }}
                        />
                    </MprcContainer>

                    <MprcFilterCount
                        loading={loading}
                        isEmpty={(viewData?.totalRows ?? 0) == 0}
                        countText={t("Student.Catalog.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                        filter={{
                            active: filtered,
                            text: t("Student.Catalog.RecordsFilteredText", { count: viewData?.totalRows ?? 0 }),
                            onResetFilter: resetFilters
                        }}
                    />

                </Box>
                <MprcContainer>
                    <MprcContainer>
                        <MprcRibbonListCardsGridContainer>
                            {
                                loading
                                    ? <GeneralCardLoading />
                                    : CardDtoToCardRibbon(
                                        viewData?.items,
                                        CarouselCardRenderMode.Default,
                                        {
                                            onFullReload: () => setReload(true),
                                            onCarouselReload: () => setReload(true),
                                        }
                                    )
                            }
                        </MprcRibbonListCardsGridContainer>
                    </MprcContainer>
                    {
                        viewData && viewData.totalPages > 1 &&
                        <Stack justifyContent="center" direction="row">
                            <Pagination
                                shape="rounded"
                                color="primary"
                                page={viewData.pageNumber}
                                count={viewData.totalPages}
                                onChange={(e, page) => setPageNumber(page)}
                            />
                        </Stack>
                    }
                </MprcContainer>
            </MprcRibbonListCardsExpandedContainer>
        </>
    );
};
